import { useEffect, useRef, useState } from 'react';
import MindElixir, { type MindElixirInstance, type MindElixirData, type NodeObj } from 'mind-elixir';
import 'mind-elixir/style';
import { ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';

/**
 * Ownership Dependency Map — everything the business depends on the owner for.
 *
 * Brought across from the standalone EOS app at `C:\Development\EOS`
 * (`frontend/src/pages/OwnershipDependencyMap.jsx`) by request, and kept
 * faithful to it: the same starting tree, the same two views over one
 * document, the same 800ms autosave, the same four statuses.
 *
 * ONE MIND-MAP TREE IS THE SINGLE SOURCE OF TRUTH, saved per organisation.
 * Two views edit the SAME tree — the Mind Map (drag, keyboard, context menu)
 * and Cards (the Owner Roles branch as areas, each holding items with a status
 * and a description). An edit in either shows up in the other.
 *
 * THREE THINGS COULD NOT COME ACROSS LITERALLY, and each is a deliberate
 * substitution rather than an oversight:
 *
 *   - The source is Tailwind (`border-red-200 bg-red-50 …`). This app has no
 *     Tailwind, so the same design is expressed in its own stylesheet.
 *   - The status palette there runs red / AMBER / GREEN / blue. This app's
 *     design system has no green and no amber at all — severity runs navy for
 *     good, then the crimson ramp — so the four statuses take that ladder
 *     instead. Same four steps, same meaning, on-brand.
 *   - The icons are `lucide-react`, which this app does not depend on. Adding
 *     an icon library for five glyphs is not worth the weight; the app's own
 *     convention of a text label carries them.
 *
 * The older SCORED dependency model — areas of items rated dependency x impact
 * — is still in the database and still read by the dashboard's per-function
 * risk figure. It is simply no longer what this page draws. See migration 082.
 */

/* First-visit content, verbatim from the source. A node spec is a string
   (leaf), [topic, children], or [topic, direction, children] for the main
   branches, which decides which side of the root they sit on. */
const { LEFT, RIGHT, SIDE } = MindElixir;

/* mind-elixir types `direction` as the literal union 0 | 1 (its LEFT/RIGHT),
   so the spec carries that rather than a bare number. */
type Direction = NonNullable<NodeObj['direction']>;
type Spec = string | [string, Spec[]] | [string, Direction, Spec[]];

const SEED_SPEC: Spec = ['Scott', [
  ['Owner Roles', RIGHT, [
    ['Sales', [
      'Sales Process Optimization',
      'Sales Tools and CRM',
      ['Market Analysis', ['Customer Segmentation', 'Competitive Analysis']],
      ['Sales Strategy', ['Sales Performance Monitoring', 'Sales Training', 'Customer Prospecting']],
    ]],
    ['HR', ['Compliance and Legal Issues', 'Conflict Resolution', 'Payroll Management',
      'Employee Engagement', 'Recruitment Coordination', 'HR Policy Development', 'Benefits']],
    ['Finance', ['Tax Planning', 'Financial Risk Management', 'Cash Flow Management',
      'Budgeting and Forecasting', 'Financial Reporting', 'PNL']],
    ['Key Account Manager', ['Key Account Growth Strategies', 'Customer Retention Programs',
      'Client Relationship Management Tools', 'Customer Feedback Analysis',
      'Account Retention Strategy', 'Regular meetings']],
  ]],
  ['Owner Responsibilities', LEFT, [
    'Decision Making Frameworks', 'Owner Stress Management', 'Risk Management',
    'Stakeholder Communication', 'Time Management', 'Strategic Planning',
    ['Approves Contract Renewals', ['Contract Renewal Process']],
    ['Approves Product Decisions', ['Product Life Cycle Management', 'Vendor Negotiations']],
    ['Approves Solution Decisions', ['Innovation and Improvement', 'Solution Evaluation Criteria']],
  ]],
  ['Owner Activities', LEFT, [
    ['Customer Engagement', ['Customer Presentation Preparation', 'Feedback Collection']],
    'Mentoring and Coaching', 'Media Relations', 'Industry Networking',
    ['Keynote Speaker at Events', ['Public Speaking Preparation', 'Event Planning']],
  ]],
]];

function buildSeedData(): MindElixirData {
  let n = 0;
  const nid = () => `odm-${++n}`;
  const toNode = (spec: Spec, isRoot = false): NodeObj => {
    if (typeof spec === 'string') return { topic: spec, id: nid() };
    const [topic, a, b] = spec as [string, Direction | Spec[], Spec[] | undefined];
    const direction = typeof a === 'number' ? (a as Direction) : undefined;
    const children = (typeof a === 'number' ? b : a) as Spec[] | undefined;
    const node: NodeObj = { topic, id: isRoot ? 'odm-root' : nid() };
    if (direction != null) node.direction = direction;
    if (children) node.children = children.map((c) => toNode(c));
    return node;
  };
  return { nodeData: toNode(SEED_SPEC, true), direction: SIDE } as MindElixirData;
}

const asText = (t: unknown): string =>
  typeof t === 'string' ? t : ((t as { text?: string })?.text ?? '');
const newId = () => `odm-${Math.random().toString(36).slice(2, 9)}`;

const STATUS_OPTIONS = ['Owner-only', 'Transitioning', 'Delegated', 'Outsourced'] as const;

/**
 * The four statuses on this app's severity ladder.
 *
 * The source app used red / amber / green / blue. The brand has no green and
 * no amber — severity runs good navy, then the crimson ramp — so the same four
 * steps are expressed in that vocabulary. Owner-only is the thing to worry
 * about, so it takes the critical end; Delegated is the good outcome and takes
 * the navy; Outsourced is fine but somebody else's, so it sits neutral.
 */
const STATUS_CLASS: Record<string, string> = {
  'owner-only': 'critical',
  transitioning: 'serious',
  delegated: 'good',
  outsourced: 'neutral',
};
const statusClass = (label: string) =>
  STATUS_CLASS[String(label).toLowerCase().replace(/[\s_]+/g, '-')] ?? 'neutral';

/* --- tree helpers, as in the source -------------------------------------- */
function findNode(root: NodeObj | null, id: string): NodeObj | null {
  if (!root) return null;
  if (root.id === id) return root;
  for (const c of root.children ?? []) {
    const f = findNode(c, id);
    if (f) return f;
  }
  return null;
}
function findParentOf(root: NodeObj | null, id: string, parent: NodeObj | null = null): NodeObj | null {
  if (!root) return null;
  if (root.id === id) return parent;
  for (const c of root.children ?? []) {
    const f = findParentOf(c, id, root);
    if (f) return f;
  }
  return null;
}
function rolesNode(root: NodeObj | null): NodeObj | null {
  return (root?.children ?? []).find((c) => /owner roles/i.test(c.topic ?? '')) ?? root;
}

interface CardItem {
  id: string;
  title: string;
  note: string;
  status: string;
  childCount: number;
}

/* Cards = the "Owner Roles" branch: each child is an area, its children are the
   items. Each item carries its status (first tag) and description (note). */
function buildCards(root: NodeObj | null): { id: string; title: string; items: CardItem[] }[] {
  const roles = rolesNode(root);
  return (roles?.children ?? []).map((area) => ({
    id: area.id,
    title: area.topic ?? '',
    items: (area.children ?? []).map((c) => ({
      id: c.id,
      title: c.topic ?? '',
      note: (c as { note?: string }).note ?? '',
      status: ((c.tags ?? []) as unknown[]).map(asText).filter(Boolean)[0] ?? '',
      childCount: (c.children ?? []).length,
    })),
  }));
}

export function OwnershipMap() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const meRef = useRef<MindElixirInstance | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [saveState, setSaveState] = useState<'saved' | 'saving' | 'dirty' | 'error'>('saved');
  const [errMsg, setErrMsg] = useState('');
  const [view, setView] = useState<'map' | 'cards'>('map');
  const [tree, setTree] = useState<NodeObj | null>(null);
  const [editing, setEditing] = useState<{ areaId: string | null; item: CardItem | null } | null>(null);

  /* Declared as refs so the effect below can call them without listing them as
     dependencies and tearing the map down on every render. */
  const saveRef = useRef<() => Promise<void>>(async () => {});
  saveRef.current = async () => {
    const me = meRef.current;
    if (!me) return;
    setSaveState('saving');
    try {
      /* getDataString strips mind-elixir's internal parent references, which
         are circular and cannot be serialised. */
      const data = JSON.parse(me.getDataString()) as MindElixirData;
      await api.put('/dependencies/map', { data });
      setSaveState('saved');
    } catch (e) {
      setSaveState('error');
      setErrMsg(e instanceof Error ? e.message : 'Could not save the map');
    }
  };

  function scheduleSave() {
    setSaveState('dirty');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void saveRef.current(), 800);
  }

  /* An edit from the Cards view: mutate the tree, re-render the map to match,
     refresh the cards, and persist. */
  function applyCardEdit(mutator: (root: NodeObj) => void) {
    const me = meRef.current;
    if (!me) return;
    const data = JSON.parse(me.getDataString()) as MindElixirData;
    mutator(data.nodeData);
    me.refresh(data);
    setTree(data.nodeData);
    scheduleSave();
  }

  const addItem = (areaId: string, v: { title: string; status: string; description: string }) =>
    applyCardEdit((t) => {
      const area = findNode(t, areaId);
      if (!area) return;
      area.children = area.children ?? [];
      area.children.push({
        id: newId(),
        topic: v.title,
        ...(v.description ? { note: v.description } : {}),
        ...(v.status ? { tags: [v.status] } : {}),
      } as NodeObj);
    });

  const editItem = (itemId: string, v: { title: string; status: string; description: string }) =>
    applyCardEdit((t) => {
      const node = findNode(t, itemId);
      if (!node) return;
      node.topic = v.title;
      (node as { note?: string }).note = v.description;
      node.tags = v.status ? [v.status] : [];
    });

  const deleteItem = (itemId: string) =>
    applyCardEdit((t) => {
      const parent = findParentOf(t, itemId);
      if (!parent?.children) return;
      parent.children = parent.children.filter((c) => c.id !== itemId);
    });

  useEffect(() => {
    let alive = true;
    let me: MindElixirInstance | undefined;

    void (async () => {
      let data: MindElixirData;
      let isSeed = false;
      try {
        const doc = await api.get<{ data: MindElixirData | null }>('/dependencies/map');
        if (doc?.data?.nodeData) data = doc.data;
        else {
          data = buildSeedData();
          isSeed = true;
        }
      } catch (e) {
        if (alive) {
          setErrMsg(e instanceof Error ? e.message : 'Could not load the map');
          setStatus('error');
        }
        return;
      }
      if (!alive || !containerRef.current) return;

      me = new MindElixir({ el: containerRef.current, direction: SIDE, editable: true });
      me.init(data);
      meRef.current = me;
      /* Centred on the NEXT frame, not this one. mind-elixir measures its
         container to work out the offset, and immediately after init the
         canvas has been created but not laid out — the measurement comes back
         against a stale box and the tree lands off in a corner, which is
         exactly where it landed. */
      requestAnimationFrame(() => {
        if (alive) me?.toCenter();
      });
      setTree(data.nodeData);
      setStatus('ready');

      /* Persist the starting map on first visit, so the structure exists for
         everybody else rather than being re-seeded per person. */
      if (isSeed) void saveRef.current();

      /* Map edits refresh the cards and save. Deliberately no me.refresh here:
         re-rendering mid-drag clobbers the interaction the user is in. */
      me.bus.addListener('operation', () => {
        setSaveState('dirty');
        try {
          setTree((JSON.parse(me!.getDataString()) as MindElixirData).nodeData);
        } catch {
          /* the map is mid-operation; the next event carries a clean tree */
        }
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => void saveRef.current(), 800);
      });
    })();

    return () => {
      alive = false;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      try {
        me?.destroy?.();
      } catch {
        /* already gone */
      }
      meRef.current = null;
    };
  }, []);

  const cards = buildCards(tree);

  return (
    <div className="odm">
      <div className="odm-bar no-print">
        <div className="tabs">
          {(['map', 'cards'] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={view === v ? 'on' : ''}
              aria-pressed={view === v}
              onClick={() => setView(v)}
            >
              {v === 'map' ? 'Mind map' : 'Cards'}
            </button>
          ))}
        </div>

        <span className="xs subtle odm-hint">
          {view === 'map'
            ? 'Double-click a node to rename · Tab adds a child · right-click for more'
            : 'Owner Roles areas — add, edit and delete items here or in the mind map'}
        </span>

        <span className="xs odm-save">
          {status === 'error' || saveState === 'error' ? (
            <span className="odm-save-error">{errMsg}</span>
          ) : saveState === 'saving' ? (
            'Saving…'
          ) : saveState === 'dirty' ? (
            'Unsaved changes…'
          ) : (
            'Saved'
          )}
        </span>
      </div>

      {status === 'loading' && <Loading label="Loading the map" />}
      {status === 'error' && <ErrorBanner message={errMsg} />}

      <div className="odm-stage">
        {/* The map stays mounted while the cards are shown — mind-elixir holds
            the document, and tearing it down to switch view would lose the
            in-flight edit and the scroll position both. */}
        <div ref={containerRef} className={`odm-canvas${view === 'cards' ? ' is-hidden' : ''}`} />

        {view === 'cards' && status === 'ready' && (
          <div className="odm-cards">
            {cards.length === 0 && (
              <p className="muted small">
                No Owner Roles branch in the map, so there is nothing to show as cards.
              </p>
            )}
            {cards.map((area) => (
              <section className="card" key={area.id}>
                <div className="card-head row-between wrap">
                  <div className="grow">
                    <h3>{area.title}</h3>
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditing({ areaId: area.id, item: null })}
                  >
                    Add item
                  </button>
                </div>
                <div className="card-body">
                  {area.items.length === 0 ? (
                    <p className="muted small">Nothing under this area yet.</p>
                  ) : (
                    <div className="eos-list">
                      {area.items.map((item) => (
                        <div className="eos-row" key={item.id}>
                          <div className="grow">
                            <div className="title">{item.title}</div>
                            {item.note && <div className="xs muted">{item.note}</div>}
                            {item.childCount > 0 && (
                              <div className="xs subtle">
                                {item.childCount} sub-item{item.childCount === 1 ? '' : 's'}
                              </div>
                            )}
                          </div>
                          {item.status && (
                            <span className={`eos-pill ${statusClass(item.status)}`}>
                              {item.status}
                            </span>
                          )}
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => setEditing({ areaId: null, item })}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => {
                              const extra = item.childCount
                                ? ` and its ${item.childCount} sub-item${item.childCount === 1 ? '' : 's'}`
                                : '';
                              if (window.confirm(`Remove "${item.title}"${extra}?`)) deleteItem(item.id);
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <ItemDialog
          initial={editing.item}
          onClose={() => setEditing(null)}
          onSave={(v) => {
            if (editing.item) editItem(editing.item.id, v);
            else if (editing.areaId) addItem(editing.areaId, v);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ItemDialog({
  initial,
  onSave,
  onClose,
}: {
  initial: CardItem | null;
  onSave: (v: { title: string; status: string; description: string }) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [statusValue, setStatusValue] = useState(initial?.status ?? '');
  const [description, setDescription] = useState(initial?.note ?? '');

  return (
    <Modal
      title={initial ? 'Edit item' : 'New item'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!title.trim()}
            onClick={() => onSave({ title: title.trim(), status: statusValue, description })}
          >
            Save
          </button>
        </>
      }
    >
      <Field label="Item" hint="What the business depends on the owner for.">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>

      <div className="mt-4">
        <Field label="Status" hint="Where this sits on the way out of the owner's hands.">
          <select
            className="select"
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
          >
            <option value="">Not set</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <Field label="Description">
          <textarea
            className="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}
